import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView, animate } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { IMAGES } from "@/data/content";

const MEASURE_POINTS = [
  { x: 160, y: 270, label: "640 mm" },
  { x: 270, y: 210, label: "38°" },
  { x: 390, y: 145, label: "R 850" },
];

function EngineeringView() {
  return (
    <div className="absolute inset-0 bg-obsidian noise-dark">
      <svg viewBox="0 0 600 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
        {Array.from({ length: 15 }, (_, i) =>
          Array.from({ length: 10 }, (_, j) => (
            <circle key={`${i}-${j}`} cx={20 + i * 40} cy={20 + j * 40} r={1} fill="rgba(255,255,255,0.08)" />
          ))
        )}
        <polyline
          points="50,330 105,330 105,300 160,300 160,270 215,270 215,240 270,240 270,210 325,210 325,180 380,180 380,150 435,150 495,150 560,150 560,110"
          fill="none"
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="1.5"
          strokeDasharray="5 5"
        />
        {MEASURE_POINTS.map((p, i) => (
          <g key={i}>
            <motion.circle
              cx={p.x}
              cy={p.y}
              r={4}
              fill="#B02942"
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 + i * 0.35, duration: 0.4 }}
            />
            <motion.text
              x={p.x + 10}
              y={p.y - 8}
              fontSize="10"
              fill="#94A3B8"
              fontFamily="IBM Plex Mono, monospace"
              letterSpacing="1"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.65 + i * 0.35 }}
            >
              {p.label}
            </motion.text>
          </g>
        ))}
        <motion.path
          d="M 50 295 L 380 115 Q 435 112 495 112 L 560 104"
          fill="none"
          stroke="#B02942"
          strokeWidth="3.5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.8, duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.circle
          cx={560}
          cy={104}
          r={5}
          fill="#B02942"
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 3.4, type: "spring", stiffness: 300 }}
        />
      </svg>
      <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal border border-signal/40 bg-obsidian/70 backdrop-blur rounded-full px-3 py-1.5">
          02 — AIMEASURE™ Output
        </span>
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 3.6 }}
          className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/30 bg-obsidian/70 backdrop-blur rounded-full px-3 py-1.5"
        >
          Rail geometry · production-ready
        </motion.span>
      </div>
    </div>
  );
}

export default function AIMeasureDemo() {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(88);
  const [dragging, setDragging] = useState(false);
  const inView = useInView(containerRef, { once: true, margin: "-120px" });
  const swept = useRef(false);

  useEffect(() => {
    if (inView && !swept.current) {
      swept.current = true;
      const controls = animate(88, 42, {
        duration: 2.6,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (v) => setPos(v),
      });
      return () => controls.stop();
    }
  }, [inView]);

  const updateFromPointer = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => updateFromPointer(e.clientX ?? e.touches?.[0]?.clientX);
    const up = () => setDragging(false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [dragging, updateFromPointer]);

  return (
    <div data-testid="aimeasure-demo">
      <div
        ref={containerRef}
        className="relative rounded-2xl overflow-hidden border border-slate-900/10 h-[340px] sm:h-[480px] select-none touch-none cursor-ew-resize"
        onPointerDown={(e) => {
          setDragging(true);
          updateFromPointer(e.clientX);
        }}
      >
        <img
          src={IMAGES.aimeasure}
          alt="Curved staircase photographed with a smartphone"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        <div className="absolute top-5 left-5 z-[3]">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600 border border-slate-300 bg-white/80 backdrop-blur rounded-full px-3 py-1.5">
            01 — Smartphone Photo
          </span>
        </div>
        <div
          className="absolute inset-0 z-[2]"
          style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        >
          <EngineeringView />
        </div>
        <div
          className="absolute top-0 bottom-0 z-[4] w-px bg-white/80 shadow-[0_0_12px_rgba(176,41,66,0.5)]"
          style={{ left: `${pos}%` }}
        >
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center">
            <MoveHorizontal size={16} className="text-slate-700" />
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2 justify-center">
        {["No laser scanner", "No expensive equipment", "Just smartphone photography"].map((b) => (
          <span key={b} className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 border border-slate-300 rounded-full px-3.5 py-1.5">
            {b}
          </span>
        ))}
      </div>
    </div>
  );
}
